import React from "react";
import { useNavigate } from "react-router-dom";
import {
  EmailShareButton,
  WhatsappShareButton,
  FacebookShareButton,
  TwitterShareButton,
  EmailIcon,
  WhatsappIcon,
  FacebookIcon,
  TwitterIcon
} from "react-share";
import ShareIcon from "../../assets/icons/share.svg";
import CopyIcon from "../../assets/icons/copy.svg";
import CheckIcon from "../../assets/icons/checked.svg";
import OfficeIcon from '../../assets/icons/OfficeList.svg';
import HomeIcon from '../../assets/icons/HomeLIst.svg';
import OtherIcon from '../../assets/icons/OtherList.svg';
import FavouriteTrue from "../../assets/icons/favouritetrue.svg";
import FavouriteFalse from "../../assets/icons/favouritefalse.svg";
import LocationIcon from '../../assets/icons/Newmap.svg';
import InstallIcon from '../../assets/images/installblue.svg';
import InstallIconHover from '../../assets/images/installwhite.svg';
import Playstore from "../../assets/images/playstore.svg";
import Appstore from "../../assets/images/appstore.svg";
import MapAddressDetails from "../Maps/MapAddressDetails";
import { Address } from "../../API/Address/ViewAllAddedAddressByUserAsync";

interface AddressContentProps {
  address: Address | null;
  isLoading: boolean;
  successMessage: string;
  showShareOptions: boolean;
  copiedField: { type: "pamCode" | "digiPin"; value: string } | null;
  currentOS: "android" | "ios" | "web";
  isAuthenticated: () => boolean;
  handleDownload: () => void;
  formatPamCode: (address: Address) => string;
  formatPamCodeForCopy: (address: Address) => string;
  handleCopy: (value: string, type: "pamCode" | "digiPin") => void;
  HeartClick: (addressId: number, isFavourite: boolean) => void;
  handleShareClick: () => void;
  handleLocationIconClick: (latitude: number, longitude: number) => void;
  shareMessage: (address: Address) => string;
  t: (key: string) => string;
}

const AddressContent: React.FC<AddressContentProps> = ({
  address,
  isLoading,
  successMessage,
  showShareOptions,
  copiedField,
  currentOS,
  isAuthenticated,
  handleDownload,
  formatPamCode,
  formatPamCodeForCopy,
  handleCopy,
  HeartClick,
  handleShareClick,
  handleLocationIconClick,
  shareMessage,
  t
}) => {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <main className="w-full px-0 py-6">
        <div className="bg-white rounded-md p-2 md:p-4 mx-2 md:mx-4">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-500">Loading address...</p>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (!isLoading && !address) {
    return (
      <main className="w-full px-0 py-6">
        <div className="bg-white rounded-md p-2 md:p-4 mx-2 md:mx-4">
          <div className="flex flex-col items-center justify-center h-64 text-gray-600">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Address Not Found</h3>
              <p className="text-sm text-gray-600 mb-6">No address found for this PAM code. Please check the code and try again.</p>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {"Back"}
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="w-full px-0 py-4">
      <div className="bg-white rounded-md p-2 md:p-4 mx-2 md:mx-4">
        <div className="relative h-[250px] sm:h-[300px] md:h-[350px]">
          {address ? (
            <MapAddressDetails addresses={[address]} />
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <p className="text-center text-gray-500">{t("loadingMap")}</p>
              </div>
            </div>
          )}
        </div>

        {successMessage && (
          <p className="mt-4 text-center text-green-500">{successMessage}</p>
        )}

        {address ? (
          <div className="mt-2">
            <div className="p-4 bg-white rounded-xl shadow-sm relative">
              <div className="flex flex-wrap items-center justify-between gap-2 text-base md:text-lg font-bold text-gray-900 mb-3">
                <div className="flex items-center gap-2">
                  {address.addressType.toLowerCase() === "office" && (
                    <>
                      <img src={OfficeIcon} alt="Office Icon" className="w-8 h-8" />
                      <span>{t("office")}</span>
                    </>
                  )}
                  {address.addressType.toLowerCase() === "home" && (
                    <>
                      <img src={HomeIcon} alt="Home Icon" className="w-8 h-8" />
                      <span>{t("home")}</span>
                    </>
                  )}
                  {address.addressType.toLowerCase() === "other" && (
                    <>
                      <img src={OtherIcon} alt="Other Icon" className="w-8 h-8" />
                      <span>{t("other")}</span>
                    </>
                  )}
                  {!["office", "home", "other"].includes(address.addressType.toLowerCase()) && (
                    <>
                      <img src={OtherIcon} alt="Custom Type Icon" className="w-8 h-8" />
                      <span>{address.addressType}</span>
                    </>
                  )}
                </div>

                {!isAuthenticated() && (
                  <button
                    onClick={handleDownload}
                    className="group flex items-center gap-2 px-3 py-2 border-2 border-blue-100 hover:bg-blue-600 hover:text-white text-blue-600 rounded-lg transition-colors text-sm font-medium"
                    title={
                      currentOS === "android"
                        ? "Download from Play Store"
                        : currentOS === "ios"
                        ? "Download from App Store"
                        : "Download Mobile App"
                    }
                  >
                    <span>
                      {currentOS === "android"
                        ? "Play Store"
                        : currentOS === "ios"
                        ? "App Store"
                        : "Install Mobile App"}
                    </span>

                    {currentOS === "android" ? (
                      <img src={Playstore} alt="Play Store" className="w-4 h-4" />
                    ) : currentOS === "ios" ? (
                      <img src={Appstore} alt="App Store" className="w-4 h-4" />
                    ) : (
                      <>
                        <img
                          src={InstallIcon}
                          alt="Download"
                          className="w-4 h-4 block group-hover:hidden"
                        />
                        <img
                          src={InstallIconHover}
                          alt="Download"
                          className="w-4 h-4 hidden group-hover:block"
                        />
                      </>
                    )}
                  </button>
                )}
              </div>

              <div className="space-y-1 text-sm md:text-base text-gray-700">
                <p>
                  <span className="font-medium text-gray-600">{t("buildingNumber")}:</span>{" "}
                  {address.addressLine1}
                </p>
                <p>
                  <span className="font-medium text-gray-600">{t("street")}:</span>{" "}
                  {address.addressLine2}
                </p>
                <p>
                  <span className="font-medium text-gray-600">{t("city")}:</span>{" "}
                  {address.addressLine3}
                </p>
                <p>
                  <span className="font-medium text-gray-600">{t("postalCode")}:</span>{" "}
                  {address.pinCode}
                </p>
              </div>

              <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-6 mt-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full xl:w-auto">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <label
                      htmlFor="pamCode"
                      className="text-sm md:text-base font-bold text-gray-600 whitespace-nowrap w-24"
                    >
                      {t("pamCode")}:
                    </label>
                    <div className="relative flex-1 sm:w-auto md:w-[180px] lg:w-[200px]">
                      <input
                        id="pamCode"
                        type="text"
                        value={formatPamCode(address)}
                        className="text-sm md:text-base p-2 pr-10 border border-gray-300 rounded-md w-full 
                          focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                        readOnly
                      />
                      <button
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-blue-500"
                        aria-label="Copy PAM Code"
                        onClick={() => handleCopy(formatPamCode(address), "pamCode")}
                      >
                        <img
                          src={
                            copiedField?.type === "pamCode" &&
                            copiedField.value === formatPamCode(address)
                              ? CheckIcon
                              : CopyIcon
                          }
                          alt="Copy"
                          className="w-5 h-5"
                        />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <label
                      htmlFor="digipin"
                      className="text-sm md:text-base font-bold text-gray-600 whitespace-nowrap w-24"
                    >
                      {t("digiPin")}:
                    </label>
                    <div className="relative flex-1 sm:w-auto md:w-[180px] lg:w-[200px]">
                      <input
                        id="digipin"
                        type="text"
                        value={address.digiPin || "N/A"}
                        className="text-sm md:text-base p-2 pr-10 border border-gray-300 rounded-md w-full 
                          focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                        readOnly
                      />
                      <button
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-blue-500"
                        aria-label="Copy DigiPin"
                        onClick={() => {
                          const digiPin = address?.digiPin?.toString().trim() || "";
                          if (digiPin && digiPin !== "N/A") {
                            handleCopy(digiPin, "digiPin");
                          }
                        }}
                      >
                        <img
                          src={
                            copiedField?.type === "digiPin" &&
                            copiedField.value === (address?.digiPin?.toString().trim() || "")
                              ? CheckIcon
                              : CopyIcon
                          }
                          alt="Copy"
                          className="w-5 h-5"
                        />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 justify-center sm:justify-start xl:justify-end items-center">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      HeartClick(Number(address.id), address.isFavourite);
                    }}
                    title={isAuthenticated() ? "Toggle Favourites" : "Login to add to favourites"}
                    className={`w-12 h-10 flex items-center justify-center border border-gray-200 bg-white rounded-lg shadow-sm 
                      ${isAuthenticated()
                        ? 'hover:bg-blue-50'
                        : 'hover:bg-gray-50 cursor-pointer'
                      } transition relative`}
                  >
                    <img
                      src={isAuthenticated() && address.isFavourite ? FavouriteTrue : FavouriteFalse}
                      alt="Favourites"
                      className="w-5 h-5"
                    />
                    {!isAuthenticated() && (
                      <div className="absolute -top-1 -right-1"></div>
                    )}
                  </button>

                  {isAuthenticated() && (
                    <button
                      onClick={handleShareClick}
                      title="Share Address"
                      className="w-12 h-10 flex items-center justify-center border border-gray-200 bg-white rounded-lg shadow-sm 
                      hover:bg-blue-50 transition"
                    >
                      <img src={ShareIcon} alt="Share" className="w-5 h-5" />
                    </button>
                  )}

                  <button
                    title="Open in Maps"
                    className="w-12 h-10 flex items-center justify-center border border-gray-200 bg-white rounded-lg shadow-sm 
                      hover:bg-blue-50 transition"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLocationIconClick(address.latitude, address.longitude);
                    }}
                  >
                    <img src={LocationIcon} alt="Location" className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {showShareOptions && (
                <div className="flex gap-4 justify-end mr-4 mt-4">
                  <EmailShareButton
                    url={shareMessage(address)}
                    className="transform transition-transform duration-300 hover:scale-110"
                  >
                    <EmailIcon size={32} round />
                  </EmailShareButton>
                  <WhatsappShareButton
                    url={shareMessage(address)}
                    className="transform transition-transform duration-300 hover:scale-110"
                  >
                    <WhatsappIcon size={32} round />
                  </WhatsappShareButton>
                  <FacebookShareButton
                    url={shareMessage(address)}
                    className="transform transition-transform duration-300 hover:scale-110"
                  >
                    <FacebookIcon size={32} round />
                  </FacebookShareButton>
                  <TwitterShareButton
                    url={shareMessage(address)}
                    className="transform transition-transform duration-300 hover:scale-110"
                  >
                    <TwitterIcon size={32} round />
                  </TwitterShareButton>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="mt-6 text-center">
            <p className="mt-6 text-center text-gray-500">{t("loadingAddressDetails")}</p>
          </div>
        )}
      </div>
    </main>
  );
};

export default AddressContent;